import { useCallback, useEffect, useMemo, useState } from "react";
import { CurrentUserHasRole } from "../api/auth";
import { apiSiteSmsAllowanceGet } from "../api/siteSmsAllowance";
import { CurrentSiteIdGet } from "../api/userSettings";
import { meterPollingInterval, roles } from "../config";
import { smsAllowanceView } from "../helpers/smsAllowance";
import useInterval from "./useInterval";

/**
 * The current site's cloud-SMS allowance, polled on the same cadence as the notifications bell.
 *
 * Read for every active site role down to SecondaryUser, which is what the endpoint enforces. A
 * dormant user settles on null and the meter stays hidden rather than the request being refused.
 */
const useSmsAllowance = () => {
	const [allowance, setAllowance] = useState(null);
	const [loading, setLoading] = useState(true);

	const refresh = useCallback(async () => {
		const siteId = CurrentSiteIdGet();
		if (!siteId || !CurrentUserHasRole(roles.SecondaryUser)) {
			setLoading(false);
			return;
		}
		try {
			const result = await apiSiteSmsAllowanceGet(siteId);
			if (result) setAllowance(result);
		} catch (error) {
			// The request opted out of the error-page redirects, so the rejection lands here. Keep the
			// last good figure rather than blanking the meter on one missed poll.
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		// Offset from the notifications bell, which fires its own first read on a random delay, so the
		// two do not always hit the backend in the same instant on every page load.
		const delay = Math.floor(Math.random() * 2000);
		const timer = setTimeout(refresh, delay);
		return () => clearTimeout(timer);
	}, [refresh]);

	useInterval(refresh, meterPollingInterval);

	const view = useMemo(() => smsAllowanceView(allowance), [allowance]);

	return { view, loading, refresh };
};

export default useSmsAllowance;
